<script setup lang="ts">
import { PRODUCTS_ROUTES } from '@/router/routes'
import { EProductActions } from '@/store/productStore/ProductStoreTypes'
import { EStoreModules } from '@/store/storeType'
import {
    DeleteButton,
    FullScreenLoader,
    HrefWrapper,
    InformationBaseCard,
    PrimaryButton,
    StackLayout,
    TitleTypography,
    TypographyWithPrefix
} from '@rfc/rfc-components-library'
import { useDetailModel } from '@rfc/rfc-components-library/hooks'
import { type DeleteButtonConfirmationDialog } from '@rfc/rfc-components-library/types'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { EProductionLineActions } from '../../../store/productionLinesStore/ProductionLineStoreTypes'
import { ERouteId } from '../../../types/enum/RouteIdEnum'
import type { ProductionLineModel } from '../../../types/models/ProductionLineModel'
import ProductionLineEditDialog from '../../../views/ProductionLineView/components/ProductionLineEditDialog.vue'

const store = useStore()
const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const productionLineId = computed(() => route.params[ERouteId.PRODUCTION_LINE_ID].toString())

const editDialogOpen = ref(false)

const productionLineModel = useDetailModel<ProductionLineModel>({
    store,
    id: productionLineId.value,
    stateKey: EStoreModules.PRODUCTION_LINE,
    getStateAction: EProductionLineActions.FETCH_PRODUCTION_LINE,
    updateStateAction: EProductionLineActions.UPDATE_PRODUCTION_LINE
})

const confirmationDialog: DeleteButtonConfirmationDialog = {
    title: t('customers.delete'),
    contentText: t('customers.deleteConfirm')
}
const handleConfirm = async () => {
    if (!productionLineModel.data.value) return
    store
        .dispatch(`${EStoreModules.PRODUCT}/${EProductActions.DELETE_PRODUCT}`, {
            id: productionLineModel.data.value?.id,
            name: productionLineModel.data.value?.name
        })
        .then(() => router.replace(PRODUCTS_ROUTES.baseUrl))
}
</script>

<template>
    <StackLayout direction="column" :gap="16" class="w-full h-full pa-4">
        <StackLayout class="align-center justify-space-between">
            <StackLayout :gap="8" class="align-center">
                <HrefWrapper icon="mid mdi-arrow-left" :href="PRODUCTS_ROUTES.baseUrl" />
                <TitleTypography :title="$t('productionLine.detail')" />
            </StackLayout>
            <StackLayout v-if="productionLineModel.storedData.value" :gap="8">
                <PrimaryButton :label="$t('common.edit')" @click="editDialogOpen = true" />
                <DeleteButton
                    :label="$t('productionLine.delete')"
                    :confirmationDialog="confirmationDialog"
                    @click="handleConfirm" />
            </StackLayout>
        </StackLayout>

        <FullScreenLoader :loading="productionLineModel.loading.value" />

        <InformationBaseCard
            v-if="productionLineModel.data.value"
            :label="productionLineModel.data.value?.name">
            <StackLayout direction="column" :gap="8">
                <slot name="detail" :data="productionLineModel.data.value" />
                <TypographyWithPrefix
                    :prefix="$t('productionLine.model.name')"
                    :label="productionLineModel.data.value?.name"
                    boldLabel />
            </StackLayout>
        </InformationBaseCard>

        <ProductionLineEditDialog
            :open="!!editDialogOpen"
            :data="productionLineModel.storedData.value ?? {}"
            @confirm="productionLineModel.saveModifications"
            @close="editDialogOpen = false">
            <slot
                name="edit"
                :data="productionLineModel.data.value"
                @change="productionLineModel.handleUpdateValue" />
        </ProductionLineEditDialog>
    </StackLayout>
</template>
